// Direct-call store (Layer B3): the ringing state that lives ABOVE any page —
// an incoming call must surface wherever you are in the app.
//
// Outside the React tree (§9.3.1): the presence socket writes here, the call
// surfaces read snapshots. Metadata only — the grant is handed to the meeting
// page separately (see callHandoff), never parked in UI state.

import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';

import type { CallInfo } from '../api';

export interface CallsView {
  /** Someone is ringing us (their device fired call.incoming). */
  incoming: CallInfo | null;
  /** The call we are currently in (set when either side answers). */
  active: CallInfo | null;
  /** Transient one-liner for the outgoing surface ("Declined", "No answer"). */
  outcome: string | null;
  /** A call that just ended remotely — the call screen watches this to leave a
   * dead call instead of sitting in an empty room. */
  ended: { callID: string; reason: string } | null;
  /** Missed incoming calls not yet seen on the Calls page (nav badge). */
  missedUnseen: number;
}

const initial: CallsView = {
  incoming: null,
  active: null,
  outcome: null,
  ended: null,
  missedUnseen: 0,
};

export const callsStore = createStore<CallsView>(() => ({ ...initial }));

export function useCallsStore<T>(selector: (s: CallsView) => T): T {
  return useStore(callsStore, selector);
}

export function setIncomingCall(call: CallInfo | null): void {
  callsStore.setState({ incoming: call });
}

export function setActiveCall(call: CallInfo | null): void {
  callsStore.setState({ active: call, incoming: null });
}

/** Show a short outcome on the outgoing surface, then clear it. */
export function setCallOutcome(text: string | null): void {
  callsStore.setState({ outcome: text });
}

/** Drop everything for one call id (whichever slot it occupies). */
export function clearCall(callID: string): void {
  callsStore.setState((s) => ({
    incoming: s.incoming?.id === callID ? null : s.incoming,
    active: s.active?.id === callID ? null : s.active,
  }));
}

/** Mark a call as finished so the call screen can leave with a reason shown. */
export function setCallEnded(callID: string, reason: string): void {
  callsStore.setState({ ended: { callID, reason } });
}

export function clearCallEnded(): void {
  callsStore.setState({ ended: null });
}

export function bumpMissed(): void {
  callsStore.setState((s) => ({ missedUnseen: s.missedUnseen + 1 }));
}

/** Called when the user opens the Calls page. */
export function clearMissed(): void {
  callsStore.setState({ missedUnseen: 0 });
}

/** Seed the badge from history at startup (calls missed while signed out). */
export function setMissedUnseen(n: number): void {
  callsStore.setState({ missedUnseen: Math.max(0, n) });
}

export function resetCalls(): void {
  callsStore.setState({ ...initial });
}

